import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import { CheckCircle2, ListChecks, XCircle } from 'lucide-react';

interface QuizResultsModeProps {
  mode?: 'exam' | 'survey';
  scorePct: number | null;
  passingScore: number;
  passed: boolean | null;
  domainScores: Record<string, number>;
  weakDomains: string[];
  timeTakenS?: number;
  answeredCount?: number;
  onGenerateDrill?: () => void;
  onReviewAnswers?: () => void;
}

function formatDomain(domain: string) {
  return domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function QuizResultsMode({
  mode = 'exam',
  scorePct,
  passingScore,
  passed,
  domainScores,
  weakDomains,
  timeTakenS,
  answeredCount,
  onGenerateDrill,
  onReviewAnswers,
}: QuizResultsModeProps) {
  const minutes = timeTakenS ? Math.round(timeTakenS / 60) : null;
  const isSurvey = mode === 'survey';

  if (isSurvey) {
    const totalPicks = Object.values(domainScores).reduce((a, b) => a + b, 0);
    return (
      <div className="h-full min-h-0 overflow-y-auto p-5 space-y-5" data-testid={testIds.quizDocumentRenderer.resultsMode}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-teal-500/15 text-teal-600">
            <ListChecks className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-semibold">Survey complete</h3>
            <p className="text-sm text-muted-foreground">
              {answeredCount != null ? `${answeredCount} answers recorded` : 'Answers recorded'}
              {minutes != null ? ` - ${minutes} min` : ''}
            </p>
            {onReviewAnswers && (
              <Button size="sm" variant="outline" className="mt-3" onClick={onReviewAnswers}>
                Review answers
              </Button>
            )}
          </div>
        </div>

        {totalPicks > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-2">Picks by theme</h4>
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 text-left">
                    <th className="px-3 py-2 font-medium">Theme</th>
                    <th className="px-3 py-2 font-medium text-right">Picks</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(domainScores)
                    .sort((a, b) => b[1] - a[1])
                    .map(([domain, count]) => (
                      <tr key={domain} className="border-t border-border">
                        <td className="px-3 py-2">{formatDomain(domain)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{count}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  const examPassed = !!passed;
  const examScore = scorePct ?? 0;

  return (
    <div className="h-full min-h-0 overflow-y-auto p-5 space-y-5" data-testid={testIds.quizDocumentRenderer.resultsMode}>
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'w-12 h-12 rounded-full flex items-center justify-center shrink-0',
            examPassed ? 'bg-green-500/15 text-green-600' : 'bg-red-500/15 text-red-600'
          )}
        >
          {examPassed ? <CheckCircle2 className="h-6 w-6" /> : <XCircle className="h-6 w-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-semibold tabular-nums">{examScore}%</h3>
          <p className="text-sm text-muted-foreground">
            {examPassed ? 'Passed' : 'Did not pass'} (passing: {passingScore}%)
            {minutes != null ? ` - ${minutes} min` : ''}
          </p>
          {onReviewAnswers && (
            <Button size="sm" variant="outline" className="mt-3" onClick={onReviewAnswers}>
              Review answers
            </Button>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">Domain breakdown</h4>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-left">
                <th className="px-3 py-2 font-medium">Domain</th>
                <th className="px-3 py-2 font-medium text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(domainScores)
                .sort((a, b) => b[1] - a[1])
                .map(([domain, pct]) => {
                  const weak = pct < passingScore;
                  return (
                    <tr key={domain} className="border-t border-border">
                      <td className="px-3 py-2">
                        {formatDomain(domain)}
                        {weak && (
                          <Badge variant="outline" className="ml-2 text-xs text-red-600 border-red-500/30">
                            weak
                          </Badge>
                        )}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums font-medium',
                          weak ? 'text-red-600' : 'text-green-600'
                        )}
                      >
                        {pct}%
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {weakDomains.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Weak domains: {weakDomains.map(formatDomain).join(', ')}
          </p>
          {onGenerateDrill && (
            <Button size="sm" variant="outline" onClick={onGenerateDrill}>
              Generate drill for weak domains
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
